<script setup lang="ts">
import AboutInlineMarker from '@/components/about-inline-marker'
import { ABOUT_DICT } from '@/constants/about'

defineOptions({
  name: 'AboutPage',
})

function handleBeliefPointerMove(event: PointerEvent) {
  const target = event.currentTarget

  if (!(target instanceof HTMLElement)) {
    return
  }

  const rect = target.getBoundingClientRect()
  const x = ((event.clientX - rect.left) / rect.width) * 100
  const y = ((event.clientY - rect.top) / rect.height) * 100

  target.style.setProperty('--about-pointer-x', `${x}%`)
  target.style.setProperty('--about-pointer-y', `${y}%`)
}

function handleBeliefPointerLeave(event: PointerEvent) {
  const target = event.currentTarget

  if (!(target instanceof HTMLElement)) {
    return
  }

  target.style.setProperty('--about-pointer-x', '50%')
  target.style.setProperty('--about-pointer-y', '54%')
}
</script>

<template>
  <div class="about-page" lang="zh-CN">
    <main class="about-page__main">
      <section class="about-page__hero" aria-labelledby="about-title">
        <h1 id="about-title" class="about-page__title">
          <span>
            {{ ABOUT_DICT.hero.titleBeforeFirstMark
            }}<AboutInlineMarker>{{ ABOUT_DICT.hero.firstMark }}</AboutInlineMarker
            >{{ ABOUT_DICT.hero.titleAfterFirstMark }}
          </span>
          <span>
            {{ ABOUT_DICT.hero.titleBeforeSecondMark
            }}<AboutInlineMarker>{{ ABOUT_DICT.hero.secondMark }}</AboutInlineMarker
            >{{ ABOUT_DICT.hero.titleAfterSecondMark }}
          </span>
        </h1>
        <p class="about-page__body about-page__hero-body">{{ ABOUT_DICT.hero.body }}</p>
      </section>

      <section id="features" class="about-page__products" aria-labelledby="about-products-title">
        <h2 id="about-products-title" class="about-page__section-title">
          {{ ABOUT_DICT.products.heading }}
        </h2>
        <p class="about-page__muted">{{ ABOUT_DICT.products.subheading }}</p>

        <div class="about-page__product-grid">
          <article
            v-for="product in ABOUT_DICT.products.items"
            :key="product.name"
            class="about-page__product-card"
          >
            <h3>{{ product.name }}</h3>
            <p>{{ product.body }}</p>
          </article>
        </div>
      </section>

      <section
        class="about-page__belief"
        aria-label="Dreama belief"
        @pointermove="handleBeliefPointerMove"
        @pointerleave="handleBeliefPointerLeave"
      >
        <div class="about-page__belief-field" aria-hidden="true" />
        <p class="about-page__belief-text">
          <span v-for="line in ABOUT_DICT.belief.lines" :key="line">{{ line }}</span>
        </p>
      </section>

      <section id="about" class="about-page__company" aria-labelledby="about-company-title">
        <h2 id="about-company-title" class="about-page__section-title">
          {{ ABOUT_DICT.company.heading }}
        </h2>
        <p class="about-page__muted">{{ ABOUT_DICT.company.subheading }}</p>
        <p class="about-page__body about-page__company-body">{{ ABOUT_DICT.company.body }}</p>
      </section>
    </main>

    <footer class="about-page__footer">
      <p>{{ ABOUT_DICT.footer.brand }}</p>
      <p>{{ ABOUT_DICT.footer.copyright }}</p>
    </footer>
  </div>
</template>

<style scoped>
@font-face {
  font-family: 'DreamaAboutOPPO';
  src: url('../../assets/about/fonts/OPPOSans.subset.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'DreamaAboutOwners';
  src: url('../../assets/about/fonts/OwnersTRIALText-Regular.otf') format('opentype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'DreamaAboutOwners';
  src: url('../../assets/about/fonts/OwnersTRIALText-Medium.otf') format('opentype');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'DreamaAboutOwners';
  src: url('../../assets/about/fonts/OwnersTRIALText-Bold.otf') format('opentype');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

.about-page {
  --font-oppo-sans: 'DreamaAboutOPPO';
  --font-owners-text: 'DreamaAboutOwners';
  --font-display: var(--font-oppo-sans), system-ui, sans-serif;
  --font-body: var(--font-oppo-sans), system-ui, sans-serif;
  --home-brand: #06c755;
  --home-brand-hover: #047733;
  --home-brand-light: #44e775;
  --home-brand-mint: #9ef2b7;
  --home-brand-vivid: #27df72;
  --home-brand-deep: #005e26;
  --home-brand-tint: #daf3e0;
  --home-brand-bright: #65eb86;
  --home-marker: #06c755;
  --home-accent-yellow: #ffe23c;
  --home-accent-orange: #ff830f;
  --home-accent-orange-deep: #ff770f;
  --home-accent-blue: #45c7ff;
  --home-page-bg: #ffffff;
  --home-surface: #f8f8f8;
  --home-surface-alt: #f0f0f0;
  --home-ink: #000000;
  --home-ink-base: #000000;
  --home-hero-bg: #e4e4e4;
  --home-border: rgb(0 0 0 / 0.06);
  --home-ease-default: cubic-bezier(0.4, 0, 0.2, 1);
  --home-ease-out: cubic-bezier(0, 0, 0.2, 1);
  --home-page-gutter: 1.25rem;
  --about-width: 40.4375rem;
  width: 100%;
  min-height: 100vh;
  overflow-x: clip;
  color: #000;
  font-family: var(--font-body);
  text-autospace: normal;
  background: #fff;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.about-page,
.about-page :deep(*) {
  box-sizing: border-box;
}

.about-page :deep(img) {
  user-drag: none;
  -webkit-user-drag: none;
}

.about-page :deep(.fill-marker) {
  fill: var(--home-marker);
}

.about-page__main {
  width: min(var(--about-width), calc(100vw - 2 * var(--home-page-gutter)));
  padding-top: calc(env(safe-area-inset-top) + 7rem);
  margin: 0 auto;
}

.about-page__hero {
  width: min(21.6875rem, 100%);
}

.about-page__title {
  display: flex;
  flex-direction: column;
  margin: 0;
  color: #000;
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.18;
  letter-spacing: 0;
}

.about-page__title :deep(.about-marker__image) {
  bottom: -0.08em;
  height: 0.46em;
}

.about-page__body {
  margin: 0;
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.637;
  letter-spacing: 0;
}

.about-page__hero-body {
  margin-top: 1.55rem;
}

.about-page__products {
  padding-top: 6.45rem;
}

.about-page__section-title {
  margin: 0;
  color: #000;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

.about-page__muted {
  margin: 0.28rem 0 0;
  color: rgb(0 0 0 / 0.4);
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.637;
  letter-spacing: 0;
}

.about-page__product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 1.42rem;
}

.about-page__product-card {
  min-height: 13.625rem;
  padding: 1.5rem;
  background: #f6f6f6;
  border: 1px solid transparent;
  border-radius: 1rem;
  transition:
    border-color 0.24s var(--home-ease-default),
    background-color 0.24s var(--home-ease-default),
    box-shadow 0.24s var(--home-ease-default),
    transform 0.24s var(--home-ease-default);
}

.about-page__product-card:hover {
  background: #fff;
  border-color: rgb(6 199 85 / 0.24);
  box-shadow: 0 18px 46px rgb(6 199 85 / 0.1);
  transform: translateY(-3px);
}

.about-page__product-card h3 {
  margin: 0;
  color: #000;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0;
}

.about-page__product-card p {
  margin: 1.125rem 0 0;
  color: #000;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.637;
  letter-spacing: 0;
}

.about-page__belief {
  --about-pointer-x: 50%;
  --about-pointer-y: 54%;
  position: relative;
  left: 50%;
  width: min(69.125rem, calc(100vw - 2 * var(--home-page-gutter)));
  min-height: 27.25rem;
  margin-top: 3.5rem;
  overflow: hidden;
  cursor: crosshair;
  transform: translateX(-50%);
}

.about-page__belief::before {
  position: absolute;
  inset: 0;
  content: '';
  background:
    radial-gradient(
      ellipse at var(--about-pointer-x) var(--about-pointer-y),
      rgb(6 199 85 / 0.24),
      rgb(6 199 85 / 0.11) 24%,
      rgb(6 199 85 / 0.035) 52%,
      transparent 72%
    ),
    radial-gradient(ellipse at 50% 54%, rgb(6 199 85 / 0.12), transparent 66%);
  filter: blur(12px);
  transform: scale(1.02);
  transition:
    filter 0.28s var(--home-ease-default),
    opacity 0.28s var(--home-ease-default);
}

.about-page__belief::after {
  position: absolute;
  inset: 14%;
  content: '';
  opacity: 0.36;
  background-image: radial-gradient(circle, rgb(6 199 85 / 0.42) 0 1.1px, transparent 1.4px);
  background-position:
    0 0,
    12px 12px;
  background-size: 24px 24px;
  mask-image: radial-gradient(
    circle at var(--about-pointer-x) var(--about-pointer-y),
    #000 0 19%,
    rgb(0 0 0 / 0.58) 29%,
    transparent 50%
  );
  transition: opacity 0.28s var(--home-ease-default);
}

.about-page__belief:hover::before {
  filter: blur(4px);
  opacity: 1;
}

.about-page__belief:hover::after {
  opacity: 0.72;
}

.about-page__belief-field {
  position: absolute;
  inset: 50% auto auto 50%;
  width: min(42rem, 70vw);
  aspect-ratio: 1;
  border: 1px solid rgb(6 199 85 / 0.12);
  border-radius: 50%;
  opacity: 0.55;
  transform: translate(-50%, -50%);
}

.about-page__belief-field::before,
.about-page__belief-field::after {
  position: absolute;
  inset: 14%;
  content: '';
  border: 1px solid rgb(6 199 85 / 0.1);
  border-radius: 50%;
}

.about-page__belief-field::after {
  inset: 34%;
  background: rgb(255 255 255 / 0.32);
}

.about-page__belief-text {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  margin: 0;
  color: rgb(0 0 0 / 0.84);
  font-size: clamp(2rem, 3.8vw, 4.25rem);
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0;
  text-align: center;
  opacity: 0.82;
  transform: translateY(0) scale(1);
  transition:
    opacity 0.32s var(--home-ease-default),
    transform 0.32s var(--home-ease-default);
  pointer-events: none;
}

.about-page__belief-text span {
  display: block;
}

.about-page__belief:hover .about-page__belief-text {
  opacity: 1;
  transform: translateY(-2px) scale(1.01);
}

.about-page__company {
  padding-top: 2.85rem;
}

.about-page__company-body {
  margin-top: 2rem;
}

.about-page__footer {
  width: min(var(--about-width), calc(100vw - 2 * var(--home-page-gutter)));
  margin: 8.05rem auto 0;
  padding-bottom: 5rem;
  color: #bfbfbf;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.637;
  text-align: center;
  letter-spacing: 0;
  white-space: pre-wrap;
}

.about-page__footer p {
  margin: 0;
}

@media (max-width: 767px) {
  .about-page__main {
    padding-top: calc(env(safe-area-inset-top) + 7.2rem);
  }

  .about-page__title {
    font-size: clamp(2.25rem, 10vw, 2.5rem);
  }

  .about-page__products {
    padding-top: 5rem;
  }

  .about-page__product-grid {
    grid-template-columns: 1fr;
  }

  .about-page__product-card {
    min-height: 0;
  }

  .about-page__belief {
    min-height: 22rem;
    margin-top: 3.75rem;
  }

  .about-page__belief-text {
    opacity: 1;
    transform: none;
  }

  .about-page__company {
    padding-top: 4.25rem;
  }

  .about-page__footer {
    margin-top: 6rem;
    padding-bottom: 3.25rem;
  }
}

@media (min-width: 768px) {
  .about-page {
    --home-page-gutter: 2.5rem;
  }
}

@media (min-width: 1024px) {
  .about-page {
    --home-page-gutter: 5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .about-page__belief::before,
  .about-page__belief::after,
  .about-page__belief-text,
  .about-page__product-card {
    transition: none;
  }
}
</style>
